import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { useMemo, useRef, useState } from "react";
import type ReCAPTCHA from "react-google-recaptcha";
import { useNavigate } from "react-router-dom";
import { Button, Recaptcha, Spinner, Typography } from "@/components/atoms";
import { GuestBookingConfirmationState } from "@/components/pages/Guest";
import { GuestBookingSplash } from "@/components/pages/Guest/GuestBookingSplash";
import { clearStashedGuestDraft, stashGuestDraft } from "@/components/pages/Guest/guestDraftStash";
import { formatDisplayJobId } from "@/helpers/bookingHelpers";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useCreateGuestBookingMutation, useUploadGuestBookingAttachmentMutation } from "@/redux/apis/guest/guestApi";
import { CreateGuestBookingBody, GuestBookingConflict } from "@/redux/apis/guest/types";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { buildGuestBookingObject, buildIdempotencyKey } from "@/redux/slices/clientBooking/bookingPayload";
import { clearBooking, setGuestPaymentMethodId } from "@/redux/slices/clientBooking/clientBookingSlice";
import {
  selectClientBookingDraft,
  selectGuestContact,
  selectGuestPaymentMethodId,
  selectOrderedLocations,
} from "@/redux/slices/clientBooking/selectors";
import { recaptchaSiteKeySelector } from "@/redux/slices/config/selectors";
import { operatorIdSelector } from "@/redux/slices/operator/selectors";
import { addToast, clsx, formatAud } from "@/utils";
import { useBookingAttachments } from "../BookingAttachmentsContext";
import type { BookingStep } from "../BookingSummaryPanel";
import { useGuestBookingQuote } from "../hooks/useGuestBookingQuote";
import { uploadBookingAttachments } from "../uploadBookingAttachments";
import { AddressSummaryCard } from "./AddressSummaryCard";
import { GuestQuotePriceSummary } from "./GuestQuotePriceSummary";
import { GuestTermsConsent } from "./GuestTermsConsent";
import { toGroups } from "./QuotePriceSummary";
import { SummaryCard } from "./SummaryCard";
import { TripDetailsSummaryCard } from "./TripDetailsSummaryCard";
import { TrustNote } from "./TrustNote";

interface GuestPricedBookingSummaryProps {
  className?: string;
  hideHeading?: boolean;
  activeStep?: BookingStep;
  onEditStep?: (step: BookingStep) => void;
  /** Side padding for the scrollable content; the sticky footer reuses it so its divider spans the full panel width. */
  edgeClassName?: string;
}

/** Is this a 409 ACCOUNT_EXISTS conflict? The email already belongs to a completed account. */
const isAccountExists = (error: unknown): boolean => {
  const err = error as FetchBaseQueryError | undefined;
  return err?.status === 409 && (err.data as GuestBookingConflict | undefined)?.code === "ACCOUNT_EXISTS";
};

/** Uppercases the first letter (e.g. "visa" → "Visa") — Stripe returns card brands lowercase. */
const cap = (value: string): string => (value ? value[0].toUpperCase() + value.slice(1) : value);

/**
 * Guest variant of the priced Booking Summary. Same cards as the authed summary plus a
 * live guest quote (GuestQuotePriceSummary — the full itemised breakdown, same as the authed summary).
 * The submit posts to the guest booking-create endpoint with the tokenised card + reCAPTCHA
 * token and the contact fields that mint the account; the server always re-prices, so the shown quote
 * is only an estimate. "Book Trip & Create Account" gates on a complete booking object, a captured card
 * (`guestPaymentMethodId`), the guest's contact details, the "I agree" consent (unchecked by default),
 * and a reCAPTCHA token — unless the DEV-only bypass flag is set (mirrors SignUpFormClient's `!token` gate).
 *
 * Success → the guest confirmation page. A 409 ACCOUNT_EXISTS stashes the draft and routes to login so
 * the authed flow can resume it. Any other failure keeps the draft intact for a retry.
 */
export const GuestPricedBookingSummary = ({
  className,
  hideHeading = false,
  activeStep,
  onEditStep,
  edgeClassName = "px-5",
}: GuestPricedBookingSummaryProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const providerUuid = useAppSelector(operatorIdSelector);

  const locations = useAppSelector(selectOrderedLocations);
  const draft = useAppSelector(selectClientBookingDraft);
  const contact = useAppSelector(selectGuestContact);
  const guestPaymentMethodId = useAppSelector(selectGuestPaymentMethodId);
  // Per-domain reCAPTCHA site key from site config — pairs the widget with the backend's verifying secret.
  // Undefined on older backends; the Recaptcha atom then falls back to the build-time env key.
  const configSiteKey = useAppSelector(recaptchaSiteKeySelector);

  const [token, setToken] = useState<string | null>(null);
  // Consent to server-side account creation (Figma 5546-151657). Unchecked by default (explicit opt-in); gates submit.
  const [termsAgreed, setTermsAgreed] = useState(false);
  const { quote, isQuotable, isLoading: isQuoteLoading, missingFields } = useGuestBookingQuote();
  const [createGuestBooking, { isLoading: isCreating }] = useCreateGuestBookingMutation();
  const [uploadAttachment] = useUploadGuestBookingAttachmentMutation();
  // Files the guest picked in the Other Details panel (shared sheet context) — uploaded after create.
  const { files, clearFiles } = useBookingAttachments();
  // Hard-blocks a duplicate POST from clicks landing in the same frame (a duplicate here is a duplicate booking).
  const submittingRef = useRef(false);
  // Spans the WHOLE submit (create → attachment upload → navigate), unlike the mutation's `isCreating`
  // which flips back the instant create resolves — otherwise the splash/spinner vanish during the
  // upload+navigate window even though the flow isn't done. Reset in submit()'s finally on any exit.
  const [isSubmitting, setSubmitting] = useState(false);
  // Holds the in-flight request so "Cancel Booking" can abort it client-side.
  const requestRef = useRef<ReturnType<typeof createGuestBooking> | null>(null);
  // Marks a user-initiated abort so the resulting rejection is swallowed (no error toast).
  const cancelledRef = useRef(false);
  // The reCAPTCHA widget instance — reset it after a failed/aborted submit so the next attempt gets a
  // FRESH token. v2 tokens are single-use and expire in ~2 min, so resending a consumed/expired token
  // fails server verification (GB.C2, "please try again"); resetting is what makes a retry succeed.
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  /** Drop the current (now consumed/stale) reCAPTCHA token and re-arm the widget for a fresh solve. */
  const resetCaptcha = () => {
    recaptchaRef.current?.reset();
    setToken(null);
  };

  // TEMPORARILY DISABLED (DEV-1241): the RIDEMINDER domain's reCAPTCHA key is a legacy key Google now
  // refuses to verify, so server-side verification always fails (GB.C2). Hide the widget + drop the token
  // gate until the key is migrated — then flip GUEST_CAPTCHA_ENABLED back to true (and remove the matching
  // early-return in GuestBookingResource::verifyRecaptcha).
  const GUEST_CAPTCHA_ENABLED = false;
  // DEV-ONLY reCAPTCHA bypass for local dev/E2E (never active in a prod build): requires import.meta.env.DEV
  // AND the opt-in flag, since the real per-domain key isn't whitelisted for localhost. When on, the widget is
  // hidden and the token gate is satisfied.
  const skipCaptcha = !GUEST_CAPTCHA_ENABLED || (import.meta.env.DEV && import.meta.env.VITE_RIDEMINDER_SKIP_GUEST_RECAPTCHA === "true");

  const booking = useMemo(() => buildGuestBookingObject(draft), [draft]);
  const canBook = !!booking && !!guestPaymentMethodId && !!contact && termsAgreed && (!!token || skipCaptcha) && !isCreating && !isSubmitting;

  /**
   * Upload each picked file to the freshly-created guest job, authorised by the per-booking key the
   * create response returns. Never throws — the booking already exists, so a failed (or, on an older
   * backend, unsupported) attachment is surfaced as an info toast rather than failing the whole submit
   * (mirrors the authed AuthedPricedBookingSummary).
   */
  const uploadAttachments = async (jobId: number, uploadKey?: string) => {
    if (!files.length) return;
    // Guest-specific: the create response must return the per-booking upload key; without it there's
    // no way to authorise the (session-less) upload, so surface that rather than silently dropping files.
    if (!uploadKey) {
      addToast("info", "Your booking was created, but attachments couldn't be uploaded.");
      return;
    }
    await uploadBookingAttachments(files, (body) => uploadAttachment({ jobId, uploadKey, body }).unwrap());
  };

  const submit = async () => {
    if (!booking || !contact || !guestPaymentMethodId || (!token && !skipCaptcha) || !providerUuid || submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    cancelledRef.current = false;

    const body: CreateGuestBookingBody = {
      recaptcha_token: token ?? "", // server-verified; empty only under the DEV bypass
      email: contact.email,
      first_name: contact.firstName,
      last_name: contact.lastName,
      phone: contact.phone,
      title: contact.title,
      payment_method_id: guestPaymentMethodId,
      idempotency_key: buildIdempotencyKey(undefined, booking),
      booking,
    };

    const request = createGuestBooking({ providerUuid, body });
    requestRef.current = request;

    try {
      const result = await request.unwrap();
      // Upload the picked attachments to the freshly-created job. A failed upload must NOT discard the
      // booking (the account/job already exist), so this never throws — it only info-toasts on failure.
      await uploadAttachments(result.job_id, result.attachment_upload_key);
      // Snapshot the draft (contact/route/trip + the shown quote) BEFORE clearing it — the confirmation
      // renders from router state, so there are no store reads to survive clearBooking(). Price rows reuse
      // the summary's own mapping so the numbers match this step exactly.
      const snapshot: GuestBookingConfirmationState = {
        // The guest create response carries only the numeric job id, so derive the "J######" display
        // number (same format as the backend) for the confirmation heading.
        displayJobId: formatDisplayJobId(result.job_id),
        contact,
        locations,
        pickupFlightNumber: draft.pickupFlightNumber,
        pickupNotFlying: draft.pickupNotFlying,
        departureFlightNumber: draft.departureFlightNumber,
        pickupDatetime: draft.pickupDatetime,
        vehicle: draft.vehicle,
        numOfPassengers: draft.numOfPassengers,
        transferTypeLabel: draft.transferType?.label,
        // Carried (in addition to the label) so the confirmation's return-trip re-book can restore the trip.
        transferType: draft.transferType ?? undefined,
        numOfBags: draft.numOfBags,
        additionalInfo: draft.additionalInfo,
        jobRate: draft.jobRate,
        bookedForDuration: draft.bookedForDuration,
        ...(isQuotable && quote ? { priceGroups: toGroups(quote), total: formatAud(quote.total) } : {}),
        ...(draft.guestPaymentMethodCard
          ? {
              card: {
                label: `${cap(draft.guestPaymentMethodCard.brand)} ••••${draft.guestPaymentMethodCard.last4}`,
                expiry: `Expiry ${String(draft.guestPaymentMethodCard.expMonth).padStart(2, "0")}/${String(
                  draft.guestPaymentMethodCard.expYear
                ).slice(-2)}`,
              },
            }
          : {}),
      };
      clearStashedGuestDraft();
      dispatch(clearBooking());
      clearFiles();
      navigate("/guest/booking/confirmation", { state: snapshot });
    } catch (error) {
      // Any failure/abort consumes (or leaves suspect) the one-shot reCAPTCHA token — re-arm the widget
      // so the next attempt sends a fresh token instead of resending a spent one (the GB.C2 "try again").
      resetCaptcha();
      // The user backed out via "Cancel Booking" — the request was aborted; dismiss silently.
      if (cancelledRef.current) return;
      // The Stripe pm_ was attached to the (now rolled-back) customer and can't be reused — clear it (and
      // its country) so the payment step resets and the guest captures a FRESH card on retry (avoids "already used").
      dispatch(setGuestPaymentMethodId({ id: null }));
      if (isAccountExists(error)) {
        // Keep the draft (stash it) and send the guest to login — the authed flow resumes it on return.
        stashGuestDraft(draft);
        addToast("info", "You already have an account with this email. Please log in to finish your booking.");
        navigate("/login");
        return;
      }
      // Any other failure (validation, server error, network/timeout) — dismiss the splash and surface it so
      // the guest stays on payment and can retry, rather than being stuck on a spinner with no feedback.
      addToast("danger", getErrorMessages(error as Parameters<typeof getErrorMessages>[0])[0]);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
      requestRef.current = null;
    }
  };

  // "Cancel Booking" on the splash — abort the in-flight request and drop back to the payment step. Does NOT
  // cancel server-side; if it already succeeded the guest keeps the booking (acceptable per PRD).
  const cancelSubmit = () => {
    cancelledRef.current = true;
    requestRef.current?.abort();
  };

  return (
    <div className={clsx("flex min-h-full w-full flex-col", className)}>
      {(isCreating || isSubmitting) && <GuestBookingSplash onCancel={cancelSubmit} />}

      <div className={clsx("flex flex-col", edgeClassName)}>
        {!hideHeading && (
          <Typography variant="title" className="mb-4 block">
            Booking Summary
          </Typography>
        )}

        <div className="flex flex-col gap-2">
          <AddressSummaryCard
            locations={locations}
            pickupFlightNumber={draft.pickupFlightNumber}
            pickupTerminalSource={draft.pickupTerminalSource}
            departureFlightNumber={draft.departureFlightNumber}
            departureTerminalSource={draft.departureTerminalSource}
            jobVerified={draft.isJobVerified}
            onEdit={onEditStep && activeStep !== "address" ? () => onEditStep("address") : undefined}
          />
          <TripDetailsSummaryCard onEdit={onEditStep && activeStep !== "trip-details" ? () => onEditStep("trip-details") : undefined} />
          <SummaryCard
            icon="Card"
            title="Payment"
            highlighted={!!guestPaymentMethodId}
            empty={!guestPaymentMethodId}
            onEdit={onEditStep && activeStep !== "payment" ? () => onEditStep("payment") : undefined}
          >
            <Typography variant="small-regular" className="text-neutral-dark-gray">
              {guestPaymentMethodId ? "Credit card ready" : "No payment method selected yet"}
            </Typography>
          </SummaryCard>
        </div>

        <GuestQuotePriceSummary
          className="mt-4"
          quote={quote}
          isQuotable={isQuotable}
          isLoading={isQuoteLoading}
          missingFields={missingFields}
        />

        {/* mb-4 keeps ≥16px between the trust note and the sticky footer's divider when scrolled. */}
        <TrustNote className="mt-4 mb-4" />
      </div>

      <div className={clsx("sticky bottom-0 mt-auto flex flex-col gap-3 border-t border-neutral-mid-gray bg-white py-4", edgeClassName)}>
        {/* Consent + server-verified reCAPTCHA both gate submit. The widget uses the per-domain site key from
            config so it pairs with the backend's verifying secret; hidden under the DEV bypass. */}
        <GuestTermsConsent checked={termsAgreed} onChange={setTermsAgreed} />
        {!skipCaptcha && (
          <div className="flex justify-center">
            {/* onExpired: v2 tokens lapse after ~2 min — drop it so a stale token can't be submitted. */}
            <Recaptcha ref={recaptchaRef} sitekey={configSiteKey} onChange={(value) => setToken(value)} onExpired={() => setToken(null)} />
          </div>
        )}
        <Button
          variant="primary"
          size="lg"
          className="w-full"
          disabled={!canBook}
          onClick={() => void submit()}
          startIcon={isCreating || isSubmitting ? <Spinner variant="light" /> : undefined}
        >
          Book Trip &amp; Create Account
        </Button>
        <Typography variant="paragraph" className="text-center">
          You can schedule a return or other trip once your booking is confirmed
        </Typography>
      </div>
    </div>
  );
};
